import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import merge from 'lodash/merge';
import { useCallback } from 'react';
import { DeferFn, FetchOptions, PromiseFn, useAsync } from 'react-async';
import { localStorageKeys } from './constants';
import { getToken, msalInstance } from './auth-config';

localStorage.setItem(localStorageKeys.IS_REFRESH_REQUIRED, 'false');

const handleCanceledError = <T>(error: any): AxiosResponse<T> => {
  if (axios.isCancel(error) || error.name === 'CanceledError' || error.message === 'canceled') {
    return {
      data: null as unknown,
      status: 0,
      statusText: 'canceled',
      headers: null as unknown,
      config: null as unknown,
      request: null,
    } as AxiosResponse<T>;
  }

  throw error;
};

const isUnauthorizedResponse = (response?: { status?: number } | null) => response?.status === 401;

export const isUnauthorizedAuthError = (error: any) =>
  (axios.isAxiosError(error) && isUnauthorizedResponse(error.response)) ||
  isUnauthorizedResponse(error);

export const shouldRetryQuery = (failureCount: number, error: unknown, maxRetries = 3) =>
  !isUnauthorizedAuthError(error) && failureCount < maxRetries;

const withAuthHeader = async (
  config: AxiosRequestConfig = {},
  forceRefresh = false,
): Promise<AxiosRequestConfig> => {
  const accessToken = await getToken(forceRefresh);

  return {
    ...config,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${accessToken}`,
    },
  };
};

const retryWithFreshToken = async <T>(
  error: any,
  retryRequest: () => Promise<AxiosResponse<T>>,
) => {
  if (isUnauthorizedAuthError(error)) {
    return retryRequest().catch(handleCanceledError<T>);
  }

  return handleCanceledError<T>(error);
};

const setIsRefreshRequired = <T>(responsePromise: Promise<AxiosResponse<T, any>>) => {
  const isRefreshRequired = localStorage.getItem(localStorageKeys.IS_REFRESH_REQUIRED);
  if (isRefreshRequired === 'true') {
    return;
  }

  responsePromise
    .then(response => {
      if (response.statusText === 'canceled') {
        return null;
      }

      const appVersion = response.headers['build-hash-version'];
      // will be equal to null if the build version changed
      if (
        appVersion !== undefined &&
        document.querySelector(`script[src="/dist/${appVersion}"]`) === null
      ) {
        apiPost('/api/rollbar/upload-source-map', {
          version: COMMITHASH,
        });

        localStorage.setItem(localStorageKeys.IS_REFRESH_REQUIRED, 'true');
      }
    })
    .catch(() => {});
};

export async function apiFetch<T>(url: string, config: AxiosRequestConfig = {}) {
  const responsePromise = axios
    .get<T>(url, await withAuthHeader(config))
    .catch(error =>
      retryWithFreshToken<T>(error, async () =>
        axios.get<T>(url, await withAuthHeader(config, true)),
      ),
    );

  setIsRefreshRequired(responsePromise);

  return responsePromise;
}

export async function apiPost<T>(url: string, data: object, config: AxiosRequestConfig = {}) {
  const responsePromise = axios
    .post<T>(
      url,
      data,
      await withAuthHeader({
        ...config,
        headers: {
          'Content-Type': 'application/json',
          ...config.headers,
        },
      }),
    )
    .catch(error =>
      retryWithFreshToken<T>(error, async () =>
        axios.post<T>(
          url,
          data,
          await withAuthHeader(
            {
              ...config,
              headers: {
                'Content-Type': 'application/json',
                ...config.headers,
              },
            },
            true,
          ),
        ),
      ),
    );

  setIsRefreshRequired(responsePromise);

  return responsePromise;
}

export async function apiPostForm<T>(
  url: string,
  formData: FormData,
  config: AxiosRequestConfig = {},
) {
  const responsePromise = axios
    .post<T>(
      url,
      formData,
      await withAuthHeader({
        ...config,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...config.headers,
        },
      }),
    )
    .catch(error =>
      retryWithFreshToken<T>(error, async () =>
        axios.post<T>(
          url,
          formData,
          await withAuthHeader(
            {
              ...config,
              headers: {
                'Content-Type': 'multipart/form-data',
                ...config.headers,
              },
            },
            true,
          ),
        ),
      ),
    );

  setIsRefreshRequired(responsePromise);

  return responsePromise;
}

export const apiGet = apiFetch;

export async function graphApiFetch<T>() {
  const account = msalInstance.getActiveAccount();

  const response = await msalInstance.acquireTokenSilent({
    scopes: ['https://graph.microsoft.com/.default'],
    account: account || undefined,
  });

  const config = {
    headers: {
      Authorization: `Bearer ${response.accessToken}`,
      'Content-Type': 'application/json',
    },
  };

  return axios.get<T>('https://graph.microsoft.com/v1.0/me', config);
}

const fetchWithAuthToken = async (url: string, init = {}, forceRefresh = false) => {
  const token = await getToken(forceRefresh);
  return fetch(url, merge({ headers: { Authorization: `Bearer ${token}` } }, init));
};

export async function fetchWithAuth(url: string, init = {}, forceRefresh = false) {
  const response = await fetchWithAuthToken(url, init, forceRefresh);

  if (!forceRefresh && isUnauthorizedResponse(response)) {
    return fetchWithAuthToken(url, init, true);
  }

  return response;
}

export interface AuthHeader {
  Authorization: string;
}

export function useFetchWithAuth<T>(
  url: string,
  init: RequestInit = {},
  options: FetchOptions<T> = {},
) {
  const { defer, json = true, ...asyncOptions } = options;
  const isDefer =
    typeof defer === 'boolean'
      ? defer
      : ['POST', 'PUT', 'PATCH', 'DELETE'].includes(init.method?.toUpperCase() ?? '');
  const identity = JSON.stringify({ url, init, isDefer, json });

  const load = useCallback(
    async (signal: AbortSignal) => {
      const response = await fetchWithAuth(url, { signal, ...init });

      if (!response.ok) {
        throw response;
      }

      return (json ? response.json() : response) as Promise<T>;
    },
    [identity], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const promiseFn = useCallback<PromiseFn<T>>((_, { signal }) => load(signal), [load]);
  const deferFn = useCallback<DeferFn<T>>((_, __, { signal }) => load(signal), [load]);

  return useAsync<T>({
    ...asyncOptions,
    [isDefer ? 'deferFn' : 'promiseFn']: isDefer ? deferFn : promiseFn,
  });
}
